import {
  addProps,
  addDisplayName,
  addHandlers,
  addState,
  flowMax,
} from 'ad-hok'
import { FC } from 'react'
import { ReactSortable } from 'react-sortablejs'
import { CustomLink as CmsCustomLink } from 'shared/src/api/types'
import { createUseStyles } from 'react-jss'
import cx from 'classnames'
import { typedAs } from 'shared/src/utils/typedAs'
import { addApi } from 'shared/src/api'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { NextActionButton } from '../NextActionButton'
import { CustomLink } from './CustomLink'
import { CustomLinkModal } from './CustomLinkModal'
import { addToastContext } from '../toastContext'
import { Button } from 'components/Button'

type ModalState =
  | { state: 'hidden' }
  | { state: 'creating' }
  | { state: 'editing'; userCustomLink: CmsCustomLink }

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  link: {
    marginBottom: 12,
  },
  reorderButtons: {
    display: 'flex',
    marginTop: 20,
    gap: 20,
  },
})

const getNewLinkPosition = (links: CmsCustomLink[]) =>
  links.length === 0 ? 0 : Math.max(...links.map((link) => link.position)) + 1

interface Props {
  userId: number
  initialLinks: CmsCustomLink[]
  className?: string
}

export const CustomLinks: FC<Props> = flowMax(
  addDisplayName('CustomLinks'),
  addState(
    'userCustomLinks',
    'setUserCustomLinks',
    ({ initialLinks }) => initialLinks
  ),
  addState(
    'modalState',
    'setModalState',
    typedAs<ModalState>({ state: 'hidden' })
  ),
  addState('isReordering', 'setIsReordering', false),
  addState(
    'originalOrderCustomLinks',
    'setOriginalOrderCustomLinks',
    typedAs<CmsCustomLink[]>([])
  ),
  addApi,
  addToastContext,
  addTranslationHelpers,
  addProps(
    ({ userCustomLinks }) => ({
      newLinkPosition: getNewLinkPosition(userCustomLinks),
    }),
    ['userCustomLinks']
  ),
  addHandlers({
    onAddPin:
      ({ setModalState }) =>
      () => {
        setModalState({ state: 'creating' })
      },
    onEditPin:
      ({ isReordering, userCustomLinks, setModalState }) =>
      (id: number) => {
        if (isReordering) {
          return
        }

        const userCustomLink = userCustomLinks.find((link) => link.id === id)!
        setModalState({ state: 'editing', userCustomLink })
      },
    onDeletePin:
      ({ userCustomLinks, setUserCustomLinks, api, showSuccessToast, t }) =>
      (id: number) => {
        api.customLinks.delete(id)
        setUserCustomLinks(userCustomLinks.filter((pin) => pin.id !== id))
        showSuccessToast({
          title: t('admin.account.userCustomLinks.deleteSuccessTitle'),
          details: t('admin.account.userCustomLinks.deleteSuccessDetails'),
        })
      },
    onCreateOrUpdate:
      ({ userCustomLinks, setUserCustomLinks, setModalState }) =>
      (result: CmsCustomLink) => {
        const updatedLinks = userCustomLinks.map((link) =>
          link.id === result.id ? result : link
        )

        if (!updatedLinks.find((link) => link.id === result.id)) {
          updatedLinks.push(result)
        }

        setUserCustomLinks(updatedLinks)
        setModalState({ state: 'hidden' })
      },
    onEnableReorder:
      ({ userCustomLinks, setIsReordering, setOriginalOrderCustomLinks }) =>
      () => {
        setIsReordering(true)
        setOriginalOrderCustomLinks(userCustomLinks)
      },
    onSaveOrder:
      ({ userCustomLinks, setIsReordering, api }) =>
      () => {
        setIsReordering(false)

        userCustomLinks.forEach((link, index) => {
          api.customLinks.update(link.id, {
            ...link,
            position: index,
          })
        })
      },
    onCancelReorder:
      ({ setIsReordering, originalOrderCustomLinks, setUserCustomLinks }) =>
      () => {
        setIsReordering(false)
        setUserCustomLinks(originalOrderCustomLinks)
      },
    onCancelModal:
      ({ setModalState }) =>
      () => {
        setModalState({ state: 'hidden' })
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    userId,
    userCustomLinks,
    isReordering,
    setUserCustomLinks,
    onAddPin,
    onEditPin,
    onDeletePin,
    onEnableReorder,
    onSaveOrder,
    onCancelReorder,
    onCancelModal,
    onCreateOrUpdate,
    modalState,
    newLinkPosition,
    classes,
    className,
    t,
  }) => (
    <>
      <div className={cx(classes.container, className)}>
        <ReactSortable
          list={userCustomLinks}
          setList={(newList) => setUserCustomLinks(newList)}
          disabled={!isReordering}
        >
          {userCustomLinks.map((link) => (
            <CustomLink
              link={link}
              onEdit={() => {
                onEditPin(link.id)
              }}
              onDelete={() => {
                onDeletePin(link.id)
              }}
              disabled={isReordering}
              className={classes.link}
              key={link.id}
            />
          ))}
        </ReactSortable>
        <NextActionButton
          icon="add"
          rightIcon="chevron-right"
          onClick={onAddPin}
          text={t('admin.account.userCustomLinks.add')}
          disabled={isReordering}
        />
        <div className={classes.reorderButtons}>
          {isReordering ? (
            <>
              <Button
                text={t('admin.account.userCustomLinks.saveOrder')}
                onClick={onSaveOrder}
              />
              <Button
                text={t('admin.account.userCustomLinks.cancel')}
                onClick={onCancelReorder}
              />
            </>
          ) : (
            <Button
              text={t('admin.account.userCustomLinks.reorder')}
              onClick={onEnableReorder}
            />
          )}
        </div>
      </div>
      {modalState.state !== 'hidden' && (
        <CustomLinkModal
          userId={userId}
          link={
            modalState.state === 'editing'
              ? modalState.userCustomLink
              : undefined
          }
          newLinkPosition={newLinkPosition}
          onCreateOrUpdate={onCreateOrUpdate}
          onCancel={onCancelModal}
        />
      )}
    </>
  )
)
